<template>
    <div class="container">
        <div
            class="timer-summary"
            :class="{
                'bg-error': existsPendingTimers,
                'bg-success': !existsPendingTimers,
                'timer-pulse-card': existsPendingTimers
            }"
        >
            <div class="icon-text-timer">
                <div v-if="existsPendingTimers" class="circle">
                    <span class="count">{{ formatDisplayNumber(timerPendings.length) }}</span>
                </div>
                <div class="pending-text" :class="{ 'apply-padding': !existsPendingTimers }">
                    <template v-if="existsPendingTimers">
                        {{ $t(`widget.timer_op_pending`) }}
                    </template>
                    <template v-else>
                        <span class="circle-number">{{ formatDisplayNumber(timersCompleted.length) }}</span>
                        {{ $t(`widget.without_alerts`) }}
                    </template>
                </div>
            </div>
            <div class="button-container">
                <div class="icon-right-arrow">{{ $t(`widget.see_timers`) }}</div>
            </div>
        </div>
        <div class="container-list">
            <div class="timer-list">
                <div v-for="timer in existsPendingTimers ? timerPendings : timersCompleted" :key="timer.id" class="timer-item">
                    <div class="task">
                        <span
                            class="task-description"
                            :class="{
                                'icon-play': timer.type === '1' && timerPendings.length > 0,
                                'icon-check': timer.type === '2' && timerPendings.length > 0
                            }"
                            :style="{
                                '--dynamic-bg-image': timersCompleted && timerPendings.length === 0 ? `url(${timer.icon})` : ''
                            }"
                        >
                            {{ timer.name }}
                        </span>
                    </div>
                    <div v-if="existsPendingTimers" class="time">{{ timer.isManual ? '-' : formatTimerAlarm(timer.currentPendingAlarm) }}</div>
                    <div v-else class="time">
                        <template v-if="timer.isAvailable">
                            <span>{{ timer.isManual ? '-' : formatTimerAlarm(timer.currentPendingAlarm) }}</span>
                        </template>
                        <template v-else>
                            <span>{{ timer.isManual ? '-' : formatTimerAlarmDisplay(timer) }}</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatNumberLabel } from '@/utils/numberFormat'
import { soundService } from '@/services/soundService'
import { formatTimerAlarmFromStore } from '@/utils/dateFormatters'

export default {
    name: 'CardOperationalTimers',
    props: {
        details: {
            type: Object,
            required: true
        }
    },
    computed: {
        timerPendings() {
            return this.$store.getters['timer/getTimersPendings']
        },
        timersCompleted() {
            return this.$store.getters['timer/getTimersCompleted'].slice(0, 3)
        },
        existsPendingTimers() {
            return this.timerPendings.length > 0
        },
        soundEnabled() {
            return this.$store.getters['isSoundEnabled']
        }
    },
    watch: {
        timerPendings(newPendings, oldPendings) {
            if (newPendings.length > (oldPendings ? oldPendings.length : 0) && this.soundEnabled) {
                soundService.playNotificationSound('/sounds/timer-expired-alert.mp3', 1.0, 'home-widget-operational-timers')
            }
        }
    },
    mounted() {
        // Si ya hay timers pendientes al montar, el watcher no detecta la transición.
        // Se reproduce el sonido una sola vez aquí.
        this.$nextTick(() => {
            if (this.existsPendingTimers && this.soundEnabled) {
                soundService.playNotificationSound('/sounds/timer-expired-alert.mp3', 1.0, 'home-widget-operational-timers')
            }
        })
    },
    methods: {
        formatDisplayNumber(value, fractionDigits) {
            return formatNumberLabel(value, this.$i18n.locale, fractionDigits)
        },
        formatTimerAlarm(timestamp) {
            return formatTimerAlarmFromStore(timestamp, { emptyValue: '-' })
        },
        formatTimerAlarmDisplay(timer) {
            if (timer.nextAlarmText) {
                return timer.nextAlarmText
            }

            return this.formatTimerAlarm(timer.nextAlarm)
        }
    }
}
</script>

<style lang="scss" scoped>
.container {
    padding: 8px;

    .timer-summary {
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        position: relative;
        gap: 5px;
        border-radius: 8px;
        min-height: 100px;

        .icon-text-timer {
            display: flex;
            align-items: center;
            width: -webkit-fill-available;
        }

        .button-container {
            width: 30%;
            margin-right: 32px;
            div {
                background-color: white;
                color: $color-secondary-800;
                padding: 14px 38px 14px 13px;
                border-radius: 9px;
                font-size: 0.8rem;
                font-weight: bold;
                font-family: 'Avenir-Bold';
            }
        }
    }

    .bg-success {
        background-color: #157d82;
    }

    .bg-error {
        background-color: #bd1f1e;
        padding: 25px;
    }

    .circle {
        width: 6rem;
        height: 6rem;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 15px;
        position: relative;
        background-color: white;

        @media (max-width: 480px) {
            width: 4rem;
            height: 4rem;
            aspect-ratio: 1 / 1;
        }
    }

    .count {
        font-size: 28px;
        color: $color-error-500;
        font-family: $title-bold;
    }

    .pending-text {
        font-size: 28px;
        line-height: 28px;
        color: white;
        font-family: $title-bold;
        font-weight: bold;
        display: flex;
        align-items: center;
        &.apply-padding {
            padding: 32px 0px 32px 32px;

            @media (max-width: 1286px) {
                background-size: 103px !important;
                padding: 32px 0px 32px 32px;
            }
        }

        .circle-number {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 96px;
            height: 96px;
            min-width: 96px;
            min-height: 96px;
            max-width: 96px;
            max-height: 96px;
            border-radius: 48%;
            background: $color-white;
            color: $color-primary-800;
            font-size: 40px;
            font-weight: bold;
            margin-right: 24px;
        }
        @media (max-width: 900px) {
            .circle-number {
                margin-right: 5px;
                width: 80px;
                height: 80px;
                min-width: 80px;
                min-height: 80px;
            }
        }
    }

    .container-list {
        padding: 8px;
    }

    .timer-list {
        min-height: 155px;
        max-height: 300px;
        overflow-y: scroll;
    }

    #timer-list::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #e2e2e2;
    }

    #timer-list::-webkit-scrollbar {
        width: 9px;
    }

    #timer-list::-webkit-scrollbar-thumb {
        border-radius: 10px;
        background-color: #b3b3b3;
    }

    .timer-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #ddd;
        padding: 0 8px;

        .timer-icon {
            width: 30px;
        }
    }

    .task {
        display: flex;
        align-items: center;
        font-size: 16px;
        color: #333;
        padding-right: 15px;
    }

    .task-description {
        @include background($image: var(--dynamic-bg-image), $position: left center, $size: 24.5px);
        padding: 26.5px 0px 26.5px 45px;
        margin-left: 8px;
        font-family: $text-light;
    }

    .icon-play {
        @include background($image: img('circle_play_red.svg'), $position: left center, $size: 30px);
    }

    .icon-check {
        @include background($image: img('icon-sign-red.svg'), $position: left center, $size: 30px);
    }

    .icon-right-arrow {
        @include background($image: img('right_arrow_home.svg'), $position: calc(100% - 16px) center, $size: 16px);
    }

    .time {
        font-size: 19px;
        color: #333;
        font-family: $title-bold;
        padding-right: 6px;
    }

    @media (max-width: 900px) {
        .task {
            font-size: 18px;
        }
    }
}
</style>
